package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AddressSearchException
import com.evoapps.evoshift.domain.model.AddressSearchFailure
import com.evoapps.evoshift.domain.model.AddressSuggestion
import com.evoapps.evoshift.domain.model.GeoLocation
import com.evoapps.evoshift.domain.repository.AddressSearchRepository
import com.evoapps.evoshift.ui.workplaces.AddressGeocoder
import com.evoapps.evoshift.ui.workplaces.GeocodeResult
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * One opening of the location dialog: a new [opening] starts from the saved values again instead
 * of what was typed and cancelled before.
 */
data class WorkplaceLocationKey(
    val workplaceId: String,
    val initialAddress: String,
    val initialLocation: GeoLocation?,
    val opening: Int
)

/** Where the map should look: [serial] changes each time, so the same place can be shown twice. */
data class MapFocus(val location: GeoLocation, val zoom: Int?, val serial: Int)

data class WorkplaceLocationUiState(
    val address: String = "",
    val coordinates: String = "",
    /** Places matching the typed address, newest search only. */
    val suggestions: List<AddressSuggestion> = emptyList(),
    val isSuggesting: Boolean = false,
    /** The address was searched and nothing matched. */
    val noMatches: Boolean = false,
    /** Why the last search gave no answer. */
    val searchFailure: AddressSearchFailure? = null,
    /** The result of "Find from address". */
    val lookup: GeocodeResult? = null,
    val isFinding: Boolean = false,
    val mapFocus: MapFocus? = null
) {
    val location: GeoLocation? get() = GeoLocation.parse(coordinates)
    val coordinatesInvalid: Boolean get() = coordinates.isNotBlank() && location == null
}

/** How long typing must pause before the address is searched (SH-132: about 600 ms). */
data class AddressSearchTiming(val debounceMillis: Long = 600)

/**
 * The workplace location dialog (SH-110, SH-132): the address with suggestions from OpenStreetMap
 * as the manager types (at least three characters, after a pause), "Find from address" (the
 * phone's own geocoder first, OpenStreetMap otherwise), a map pin and the coordinates field, all
 * showing the same point. Nothing is saved here: the dialog hands [WorkplaceLocationUiState.address]
 * and [WorkplaceLocationUiState.location] to the settings screen's Save.
 */
class WorkplaceLocationViewModel(
    private val key: WorkplaceLocationKey,
    private val search: AddressSearchRepository,
    private val timing: AddressSearchTiming,
    private val language: () -> String
) : ViewModel() {

    private var serial = 0
    private var suggestJob: Job? = null

    private val _uiState = MutableStateFlow(
        WorkplaceLocationUiState(
            address = key.initialAddress,
            coordinates = key.initialLocation?.format().orEmpty(),
            mapFocus = key.initialLocation?.let { focus(it, POINT_ZOOM) }
        )
    )
    val uiState: StateFlow<WorkplaceLocationUiState> = _uiState.asStateFlow()

    private fun focus(location: GeoLocation, zoom: Int?) = MapFocus(location, zoom, ++serial)

    fun onAddressChange(text: String) {
        _uiState.update { it.copy(address = text, lookup = null) }
        suggest(text)
    }

    private fun suggest(text: String) {
        suggestJob?.cancel()
        val query = text.trim()
        if (query.length < AddressSearchRepository.MIN_QUERY_LENGTH) {
            _uiState.update { it.copy(suggestions = emptyList(), isSuggesting = false, noMatches = false, searchFailure = null) }
            return
        }
        suggestJob = viewModelScope.launch {
            if (timing.debounceMillis > 0) delay(timing.debounceMillis)
            _uiState.update { it.copy(isSuggesting = true) }
            val result = search.search(key.workplaceId, query, language())
            _uiState.update { state ->
                result.fold(
                    onSuccess = { places ->
                        state.copy(suggestions = places, isSuggesting = false, noMatches = places.isEmpty(), searchFailure = null)
                    },
                    onFailure = { e ->
                        state.copy(suggestions = emptyList(), isSuggesting = false, noMatches = false, searchFailure = failureOf(e))
                    }
                )
            }
        }
    }

    /** A suggestion picked: its name becomes the address and the pin moves there. */
    fun pickSuggestion(place: AddressSuggestion) {
        suggestJob?.cancel()
        _uiState.update {
            it.copy(
                address = place.label,
                coordinates = place.location.rounded().format(),
                suggestions = emptyList(),
                isSuggesting = false,
                noMatches = false,
                searchFailure = null,
                lookup = GeocodeResult.Found(place.location),
                mapFocus = focus(place.location, PLACE_ZOOM)
            )
        }
    }

    /** Typed or pasted coordinates: once they read as a point, the map follows (same zoom). */
    fun onCoordinatesChange(text: String) {
        val location = GeoLocation.parse(text)
        _uiState.update {
            it.copy(coordinates = text, lookup = null, mapFocus = location?.let { point -> focus(point, null) } ?: it.mapFocus)
        }
    }

    /** A point clicked on the map: the pin and the coordinates move there; the map stays put. */
    fun placePin(location: GeoLocation) {
        _uiState.update { it.copy(coordinates = location.rounded().format(), lookup = null) }
    }

    /**
     * "Find from address": [native] (the phone's geocoder, null on the web and the desktop) first;
     * when it finds nothing or fails, the best OpenStreetMap match. Not found by either keeps the
     * native answer (so "offline" stays "offline").
     */
    fun findCoordinates(native: AddressGeocoder?) {
        val address = _uiState.value.address.trim()
        if (address.isEmpty() || _uiState.value.isFinding) return
        suggestJob?.cancel()
        _uiState.update { it.copy(isFinding = true, lookup = null, isSuggesting = false) }
        viewModelScope.launch {
            val first = native?.locate(address)
            val result = if (first is GeocodeResult.Found) first else fromSearch(address, first)
            _uiState.update {
                if (result is GeocodeResult.Found) {
                    it.copy(
                        isFinding = false, lookup = result, coordinates = result.location.rounded().format(),
                        suggestions = emptyList(), noMatches = false, mapFocus = focus(result.location, PLACE_ZOOM)
                    )
                } else {
                    it.copy(isFinding = false, lookup = result)
                }
            }
        }
    }

    private suspend fun fromSearch(address: String, native: GeocodeResult?): GeocodeResult {
        if (address.length < AddressSearchRepository.MIN_QUERY_LENGTH) return native ?: GeocodeResult.NotFound
        return search.search(key.workplaceId, address, language()).fold(
            onSuccess = { places -> places.firstOrNull()?.let { GeocodeResult.Found(it.location) } ?: native ?: GeocodeResult.NotFound },
            onFailure = { GeocodeResult.Failed }
        )
    }

    private fun failureOf(e: Throwable): AddressSearchFailure =
        (e as? AddressSearchException)?.failure ?: AddressSearchFailure.FAILED

    companion object {
        /** Street level, for a found place. */
        const val PLACE_ZOOM = 17
        /** The saved point when the dialog opens. */
        const val POINT_ZOOM = 16
    }
}
